import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

// Ensure dayjs plugins are loaded
dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * 🔄 Registration Period Status Interface
 *
 * Defines the structure for registration period status information
 */
export interface RegistrationPeriodStatus {
  /** Current status of the registration period */
  status: 'before' | 'active' | 'after'
  /** Whether the current time is before the start date */
  isBeforeStart: boolean
  /** Whether the current time is after the end date */
  isAfterEnd: boolean
  /** Human-readable message describing the current status */
  message: string
}

/**
 * 🔄 getRegistrationPeriodStatus: Determines if a registration is open based on date range
 *
 * This function properly handles registration periods by:
 * - Extracting date components from UTC timestamps (treating them as calendar dates)
 * - Creating local dates using the same calendar date in user's timezone
 * - Treating start date as beginning of day (00:00:00.000) in local timezone
 * - Treating end date as end of day (23:59:59.999) in local timezone
 * - Comparing against user's current local time
 *
 * This ensures that a registration with the same start and end date
 * is considered open for the entire day in the user's timezone.
 *
 * **Important**: Server dates like "2025-07-03T00:00:00.000Z" are interpreted
 * as "July 3rd" in the user's local timezone, not converted to the previous day.
 *
 * @param startsAtIso - ISO-8601 date string (UTC) when registration starts
 * @param endsAtIso - ISO-8601 date string (UTC) when registration ends
 * @param now - Current date/time for comparison (defaults to current local time)
 * @returns RegistrationPeriodStatus object with status and helper flags
 *
 * @example
 * ```typescript
 * // Registration on July 3rd, current time is July 3rd 10:00 AM local
 * const status = getRegistrationPeriodStatus(
 *   '2024-07-03T00:00:00.000Z',
 *   '2024-07-03T23:59:59.999Z'
 * )
 * // Returns: { status: 'active', isBeforeStart: false, isAfterEnd: false, message: '' }
 *
 * // Registration not yet started
 * const status = getRegistrationPeriodStatus(
 *   '2024-07-05T00:00:00.000Z',
 *   '2024-07-10T23:59:59.999Z'
 * )
 * // Returns: { status: 'before', isBeforeStart: true, isAfterEnd: false, message: '...' }
 * ```
 */
export function getRegistrationPeriodStatus(
  startsAtIso: string | null | undefined,
  endsAtIso: string | null | undefined,
  now: dayjs.Dayjs = dayjs()
): RegistrationPeriodStatus {
  console.log('🔄 getRegistrationPeriodStatus: Checking registration period', {
    startsAtIso,
    endsAtIso,
    currentTime: now.format(),
    currentTimeLocal: now.local().format(),
  })

  // If no dates provided, assume registration is always active
  if (!startsAtIso || !endsAtIso) {
    console.log(
      '🔄 getRegistrationPeriodStatus: No dates provided, assuming active'
    )
    return {
      status: 'active',
      isBeforeStart: false,
      isAfterEnd: false,
      message: '',
    }
  }

  // Extract date components from UTC timestamps and apply to user's local timezone
  // This ensures that "2025-07-03T00:00:00.000Z" becomes July 3rd in user's local timezone
  // rather than being converted to the previous day
  const startUtc = dayjs.utc(startsAtIso)
  const endUtc = dayjs.utc(endsAtIso)

  // Create local dates using the same year/month/day as the UTC dates
  // Start date: beginning of day (00:00:00.000) in user's local timezone
  // End date: end of day (23:59:59.999) in user's local timezone
  const startLocal = dayjs()
    .year(startUtc.year())
    .month(startUtc.month())
    .date(startUtc.date())
    .startOf('day')

  const endLocal = dayjs()
    .year(endUtc.year())
    .month(endUtc.month())
    .date(endUtc.date())
    .endOf('day')

  console.log('🔄 getRegistrationPeriodStatus: Date extraction', {
    startUtcDate: `${startUtc.year()}-${
      startUtc.month() + 1
    }-${startUtc.date()}`,
    endUtcDate: `${endUtc.year()}-${endUtc.month() + 1}-${endUtc.date()}`,
    startLocalCreated: startLocal.format(),
    endLocalCreated: endLocal.format(),
  })

  console.log('🔄 getRegistrationPeriodStatus: Date comparison', {
    startLocal: startLocal.format(),
    endLocal: endLocal.format(),
    now: now.format(),
    isBeforeStart: now.isBefore(startLocal),
    isAfterEnd: now.isAfter(endLocal),
  })

  // Check if current time is before start date
  if (now.isBefore(startLocal)) {
    console.log('🔄 getRegistrationPeriodStatus: Registration not yet started')
    return {
      status: 'before',
      isBeforeStart: true,
      isAfterEnd: false,
      message:
        'Registration is not currently open. Please check back later for updates.',
    }
  }

  // Check if current time is after end date
  if (now.isAfter(endLocal)) {
    console.log('🔄 getRegistrationPeriodStatus: Registration has ended')
    return {
      status: 'after',
      isBeforeStart: false,
      isAfterEnd: true,
      message:
        'Registration is closed. Please check back next season for updates.',
    }
  }

  // Registration is currently active
  console.log('🔄 getRegistrationPeriodStatus: Registration is active')
  return {
    status: 'active',
    isBeforeStart: false,
    isAfterEnd: false,
    message: '',
  }
}
